import { z } from 'zod';

/**
 * Buyer tier union DTOs (spec 5.2, SIL-1314). Deliberately dependency-free
 * (no import from ./listing.js or ./marketplace.js): both of those modules
 * import these schemas, and either one importing the other back would be a
 * circular import that throws at module-init time ("Cannot read properties
 * of undefined") because of the top-level `z.object()` evaluation order.
 *
 * SIL-1328: pricing. An unverified buyer never receives an exact price or
 * FMV value; the range is bucketed (review #7) so it never narrows enough to
 * reveal the exact price. A verified buyer (or the listing owner) gets exact
 * values, the FMV band and pct vs FMV.
 *
 * SIL-1331: evidence and the per-node lot member shape. An unverified buyer
 * sees only booleans, the region and node count; a verified buyer (or the
 * owner) sees the site, per-run counts, tier and dates, a custody summary,
 * and per-node lot member detail (chassis serial, verdict).
 *
 * SIL-1345: `avState` — the THIRD Active Verification state, on BOTH tiers.
 * See `avStateSchema` below.
 */
export type BuyerTier = 'verified' | 'unverified';

/**
 * SIL-1345 (SIL-1318's own note): the Active Verification state is THREE-valued,
 * not a boolean.
 *
 *   'active'  — a results_ready run inside the 90-day validity window.
 *   'expired' — the newest run has aged out of the window.
 *   'none'    — no run has ever completed for any node behind the listing.
 *
 * `hasActiveVerification` alone collapses 'expired' and 'none' together, and a
 * renderer branching on a single boolean has no way to avoid implying a
 * FAILURE for either. The card, the lot table and the drawer must distinguish
 * them, and NEITHER 'expired' nor 'none' may use the Verified mark. Sourced
 * from `listing_av_state()` (migration 962), which is the only place the
 * 90-day window is evaluated for this purpose.
 */
export type AvState = 'active' | 'expired' | 'none';
export const avStateSchema = z.enum(['active', 'expired', 'none']);
export type MarketLabel = 'below' | 'at' | 'above';
export interface PriceRange {
  lo: number;
  hi: number;
}
export interface FmvBandDto {
  low: number;
  median: number;
  high: number;
  comps: number;
}

export const priceRangeSchema = z.object({ lo: z.number(), hi: z.number() });
export const fmvBandDtoSchema = z.object({
  low: z.number(),
  median: z.number(),
  high: z.number(),
  comps: z.number().int(),
});

export interface UnverifiedPricing {
  tier: 'unverified';
  priceRange: { perGpu: PriceRange | null; perNode: PriceRange; total: PriceRange };
  marketLabel: MarketLabel | null;
}
export interface VerifiedPricing {
  tier: 'verified';
  /** SIL-1578: null when the GPU count behind the price is unknown (mixed lot). */
  pricePerGpuUsd: number | null;
  pricePerNodeUsd: number;
  totalUsd: number;
  fmvBand: FmvBandDto | null;
  pctVsFmv: number | null;
}

export const unverifiedPricingSchema = z.object({
  tier: z.literal('unverified'),
  priceRange: z.object({ perGpu: priceRangeSchema.nullable(), perNode: priceRangeSchema, total: priceRangeSchema }),
  marketLabel: z.enum(['below', 'at', 'above']).nullable(),
});
export const verifiedPricingSchema = z.object({
  tier: z.literal('verified'),
  pricePerGpuUsd: z.number().nullable(),
  pricePerNodeUsd: z.number(),
  totalUsd: z.number(),
  fmvBand: fmvBandDtoSchema.nullable(),
  pctVsFmv: z.number().nullable(),
});
/**
 * SIL-1480 (spec section 2/4, price_display='ask'): a single exact asking
 * price to BOTH buyer tiers -- no range, no FMV band, no market label, no
 * pct-vs-FMV. Distinct from `VerifiedPricing` (which still carries
 * `fmvBand`/`pctVsFmv`) precisely because the whole point of 'ask' mode is
 * suppressing market context, not just widening who sees the exact price.
 * The asking price itself is computed FMV-only (never source ask + markup)
 * so a buyer of either tier can never infer the arbitrage source ask from it.
 */
export interface AskPricing {
  tier: 'ask';
  priceUsd: number;
}
export const askPricingSchema = z.object({
  tier: z.literal('ask'),
  priceUsd: z.number(),
});
export const pricingSchema = z.discriminatedUnion('tier', [
  unverifiedPricingSchema,
  verifiedPricingSchema,
  askPricingSchema,
]);

export interface UnverifiedEvidence {
  tier: 'unverified';
  hasActiveVerification: boolean;
  /** SIL-1345 — 'active' | 'expired' | 'none'. `hasActiveVerification` is
   * exactly `avState === 'active'`; read `avState` to tell an aged-out run
   * apart from a listing that was never verified. Never a failure signal. */
  avState: AvState;
  isSelfReported: boolean;
  custodyUnbroken: boolean;
  nodeCount: number;
  region: string | null;
  /**
   * SIL-1444: distinct asset_class_id slugs behind the listing (migration
   * 975). UNGATED on both tiers — the marketplace asset-type filter and
   * per-card badges need this regardless of buyer verification.
   */
  assetClassIds: string[];
}
export interface VerifiedEvidence {
  tier: 'verified';
  site: string | null;
  /**
   * SIL-1444: data_center.address->>'state' (migration 975) — state/region
   * of the data center, NOT the facility name (`site`, above). Null when the
   * address has no state recorded; the marketplace card's LOCATION cell
   * falls back to "Not stated", never to `site`.
   */
  siteState: string | null;
  region: string | null;
  /** SIL-1444: same as `UnverifiedEvidence.assetClassIds` — ungated. */
  assetClassIds: string[];
  /** SIL-1345 — same three-valued state as the unverified tier. A verified
   * buyer can also read `verificationSummary.validTo`, but the pass/obs/fail
   * counts are window-scoped and go to zero for BOTH 'expired' and 'none';
   * only this field separates them. */
  avState: AvState;
  verificationSummary: {
    pass: number;
    obs: number;
    fail: number;
    nodes: number;
    unverifiedNodes: number;
    tier: 1 | 2 | 3 | null;
    runAt: string | null;
    validTo: string | null;
  };
  custodySummary: { events: number; owners: number; unbroken: boolean; lastEventAt: string | null };
}

export const unverifiedEvidenceSchema = z.object({
  tier: z.literal('unverified'),
  hasActiveVerification: z.boolean(),
  avState: avStateSchema,
  isSelfReported: z.boolean(),
  custodyUnbroken: z.boolean(),
  nodeCount: z.number().int(),
  region: z.string().nullable(),
  assetClassIds: z.array(z.string()),
});
export const verifiedEvidenceSchema = z.object({
  tier: z.literal('verified'),
  site: z.string().nullable(),
  siteState: z.string().nullable(),
  region: z.string().nullable(),
  assetClassIds: z.array(z.string()),
  avState: avStateSchema,
  verificationSummary: z.object({
    pass: z.number().int(),
    obs: z.number().int(),
    fail: z.number().int(),
    nodes: z.number().int(),
    unverifiedNodes: z.number().int(),
    tier: z.union([z.literal(1), z.literal(2), z.literal(3)]).nullable(),
    runAt: z.string().nullable(),
    validTo: z.string().nullable(),
  }),
  custodySummary: z.object({
    events: z.number().int(),
    owners: z.number().int(),
    unbroken: z.boolean(),
    lastEventAt: z.string().nullable(),
  }),
});
export const evidenceSchema = z.discriminatedUnion('tier', [unverifiedEvidenceSchema, verifiedEvidenceSchema]);

/**
 * A lot member's per-node detail for a verified buyer (or the listing
 * owner) — chassis serial, worst-in-window verdict, evidence tier and
 * custody. Never sent to an unverified non-owner (lotMembers is null).
 */
export interface LotMemberForBuyer {
  assetId: string;
  fingerprint: string;
  chassisSerial: string | null;
  verdict: string | null;
  evidenceTier: 1 | 2 | 3 | null;
  runAt: string | null;
  custodyEvents: number;
}
export const lotMemberForBuyerSchema = z.object({
  assetId: z.string().uuid(),
  fingerprint: z.string(),
  chassisSerial: z.string().nullable(),
  verdict: z.string().nullable(),
  evidenceTier: z.union([z.literal(1), z.literal(2), z.literal(3)]).nullable(),
  runAt: z.string().nullable(),
  custodyEvents: z.number().int(),
});

// ---------------------------------------------------------------------------
// SIL-1345 — commercial terms + technical details (spec 3.1/3.2)
// ---------------------------------------------------------------------------

/** SIL-1345 — `listing.payment_terms`, mirroring migration 962's CHECK. */
export type PaymentTerms = 'escrow' | 'wire_on_acceptance' | 'net_30';
export const paymentTermsSchema = z.enum(['escrow', 'wire_on_acceptance', 'net_30']);

/**
 * SIL-1345 — the buyer-safe commercial terms, on BOTH tiers: they are part of
 * the offer terms, same posture as `availableOn`. `null` means "not stated" and
 * the UI hides the tile rather than rendering "unknown".
 *
 * `minOfferUsd` is DELIBERATELY ABSENT from this shape and from every buyer
 * DTO. It is the seller's walk-away number; see `listingOwnerTermsSchema`.
 *
 * `purchasePending` (SIL-1426, spec s.4): true when an OPEN (status='pending')
 * buy_now offer already exists on this listing — the DB partial unique index
 * `offer_one_open_buy_now_per_listing` (migration 972) allows at most one, so
 * this is a boolean, not a count. On BOTH tiers (same posture as the rest of
 * this shape) so the UI can disable the Buy now button before the request
 * round-trips and hits 409 PURCHASE_PENDING.
 */
export interface ListingCommercialTerms {
  shipsWithinDays: number | null;
  paymentTerms: PaymentTerms | null;
  purchasePending: boolean;
  /**
   * SIL-1472 — `listing.offers_only`. When true, Buy now is never available
   * for this listing (an offer is the only buyer action) — enforced in the
   * DB (`tg_offer_buy_now_insert`, 979), not only hidden here. Buyer-safe on
   * both tiers, same posture as the rest of this shape. Default false —
   * every pre-SIL-1472 listing is unaffected.
   */
  offersOnly: boolean;
  /**
   * SIL-1480: server-computed "Verified seller" signal -- a boolean ONLY,
   * never a seller name or id. Driven by the house org (`customer_org.is_house`,
   * migration 980) today -- the platform has no other seller-org
   * verification/passport-tier signal yet (spec: "If the platform already
   * has a seller-org verification signal ... use it; otherwise show it only
   * for the house org"). Buyer-safe on both tiers, same posture as
   * `offersOnly`: this is a positive marker, not an identity disclosure.
   */
  sellerVerified: boolean;
}
export const listingCommercialTermsSchema = z.object({
  shipsWithinDays: z.number().int().nullable(),
  paymentTerms: paymentTermsSchema.nullable(),
  purchasePending: z.boolean(),
  offersOnly: z.boolean(),
  sellerVerified: z.boolean(),
});

/**
 * SIL-1345 — OWNER-ONLY listing terms. `minOfferUsd` is the seller's offer
 * floor: revealing it hands every buyer the walk-away number, so it is
 * seller-private at three layers — no column-level SELECT for client_readonly
 * (migration 962), `client_listing_min_offer_usd()` returns NULL to a
 * non-owner, and `projectOwnerTerms()` OMITS THE KEY ENTIRELY (not `null`) for
 * any non-owner. The key-absence is load-bearing: a `null` would still tell a
 * buyer the field exists and invite a UI that renders a placeholder for it.
 */
export interface ListingOwnerTerms {
  minOfferUsd?: number;
}
export const listingOwnerTermsSchema = z.object({
  minOfferUsd: z.number().positive().optional(),
});

/**
 * SIL-1345 — technical detail tiles (spec 3.2). Sourced from the SKU's
 * `class_attributes`. EVERY field is optional and a missing attribute is
 * OMITTED from the payload rather than sent as `null`: the UI hides a tile it
 * has no key for, and "omitted" vs. "null" is the difference between hiding a
 * tile and rendering an empty one. See `technicalDetailsFromClassAttributes`
 * (packages/shared/src/listing-technical-details.ts) — the single parser.
 */
export interface ListingTechnicalDetails {
  gpuModel?: string;
  architecture?: string;
  gpusPerNode?: number;
  serverModel?: string;
  cpu?: string;
  systemMemoryGb?: number;
  networking?: string;
  gpuMemoryGb?: number;
}
export const listingTechnicalDetailsSchema = z.object({
  gpuModel: z.string().optional(),
  architecture: z.string().optional(),
  gpusPerNode: z.number().optional(),
  serverModel: z.string().optional(),
  cpu: z.string().optional(),
  systemMemoryGb: z.number().optional(),
  networking: z.string().optional(),
  gpuMemoryGb: z.number().optional(),
});
